import React, { useEffect, useState, useRef } from 'react';

import FileCopyIcon from '@mui/icons-material/FileCopy';
import CircularProgress from '@mui/material/CircularProgress';
import {
    Box,
    Button,
    Grid,
    Stack,
    Tooltip,
    Typography,
} from '@sunwisesoftware/sunwise-ui';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Field, reduxForm, change } from 'redux-form';
import { createStructuredSelector } from 'reselect';

import { ShowErrors } from 'common/components';
import {
    ReduxFieldDatePicker,
    ReduxFieldInput,
    ReduxFieldSelect,
    ReduxFieldSwitch,
} from 'common/components/form/redux';

import * as actions from '../actions';
import { NAME } from '../constants';
import { getIsOwner } from '../helpers';
import * as selectors from '../selectors';
import validate from '../validate';

const UserForm = ({
    changeInput,
    errors,
    fetchRandomPassword,
    formValues,
    handleClickSave,
    handleSubmit,
    initialValues,
    isFetchingDetail,
    isFetchingPassword,
    isSaving,
    prepareDelete,
    roles,
    rolesDictionary,
}) => {
    const { t } = useTranslation();
    const inputRef = useRef(null);
    const [showTemporalPassword, setTemporalPassword] = useState(false);
    const [actionTitle, setActionTitle] = useState('Copy');

    useEffect(() => {
        changeInput(
            'isOwner',
            getIsOwner(rolesDictionary, formValues.company_group),
        );
    }, [formValues.company_group]);

    const handleGeneratePassword = () => {
        fetchRandomPassword((tempPass) => {
            changeInput('tmp_password', tempPass);
        });
    };

    const handleDeleteUser = (item) => {
        prepareDelete(item);
    };

    const groupsFiltered = (options) => {
        return options
            ? options.map((group) => {
                  let options = {};
                  options['label'] = group.name;
                  options['optionValue'] = group.id;
                  options['disabled'] = false;
                  return options;
              })
            : [];
    };

    const levelsFiltered = groupsFiltered(roles);

    return (
        <form>
            <Stack pt={2}>
                <Field
                    component={ReduxFieldInput}
                    disabled={isFetchingDetail}
                    label={t('Name').concat('*')}
                    name="first_name"
                    variant="outlined"
                />
            </Stack>

            <Grid container spacing={2} pt={2}>
                <Grid size={{ md: 9, xs: 18 }}>
                    <Field
                        name="last_name"
                        component={ReduxFieldInput}
                        label={t('Last name').concat('*')}
                        variant="outlined"
                        disabled={isFetchingDetail}
                    />
                </Grid>

                <Grid size={{ md: 9, xs: 18 }}>
                    <Field
                        name="second_surname"
                        component={ReduxFieldInput}
                        label={t('Second surname')}
                        variant="outlined"
                        disabled={isFetchingDetail}
                    />
                </Grid>
            </Grid>

            <Grid container spacing={2} pt={2}>
                <Grid size={{ md: 9, xs: 18 }}>
                    <Field
                        name="birth_date"
                        component={ReduxFieldDatePicker}
                        label={t('Date of birth')}
                        variant="outlined"
                        disabled={isFetchingDetail}
                        maxDate={new Date()}
                        fullWidth
                    />
                </Grid>

                <Grid size={{ md: 9, xs: 18 }}>
                    <Field
                        name="phone"
                        component={ReduxFieldInput}
                        label={t('Phone')}
                        variant="outlined"
                        disabled={isFetchingDetail}
                    />
                </Grid>
            </Grid>

            <Stack spacing={2} pt={2}>
                <Field
                    name="position"
                    component={ReduxFieldInput}
                    label={t('Position')}
                    variant="outlined"
                    disabled={isFetchingDetail}
                />

                <Field
                    name="email"
                    component={ReduxFieldInput}
                    label={t('Mail').concat('*')}
                    type="email"
                    variant="outlined"
                    disabled={isFetchingDetail}
                />
            </Stack>

            <Grid container spacing={2} pt={2}>
                <Grid size={{ md: 9, xs: 18 }}>
                    <Field
                        name="company_group"
                        component={ReduxFieldSelect}
                        disabled={isFetchingDetail}
                        label={t('Role').concat('*')}
                        variant="outlined"
                        options={levelsFiltered}
                    />
                </Grid>
            </Grid>

            <Stack spacing={2} pt={2}>
                <Field
                    name="has_tmp_password"
                    component={ReduxFieldSwitch}
                    label={t('Temporal password')}
                    disabled={
                        isFetchingDetail || !formValues.allow_staff_support
                    }
                    onChange={(event) => {
                        setTemporalPassword(event.target.checked);
                        if (event.target.checked) {
                            handleGeneratePassword();
                        } else changeInput('tmp_password', '');
                    }}
                />

                {showTemporalPassword && (
                    <>
                        {isFetchingPassword ? (
                            <Stack
                                direction="row"
                                justifyContent="center"
                                alignItems="center"
                                spacing={2}
                            >
                                <Typography>
                                    {t('Generating password')}...
                                </Typography>
                                <CircularProgress size={25} />
                            </Stack>
                        ) : (
                            <Field
                                inputRef={inputRef}
                                name="tmp_password"
                                component={ReduxFieldInput}
                                label={t('Password').concat('*')}
                                variant="outlined"
                                endAdornmentIcon={
                                    <Tooltip
                                        title={t(actionTitle)}
                                        placement="top"
                                    >
                                        <FileCopyIcon />
                                    </Tooltip>
                                }
                                handleClickButtonEndAdornment={() => {
                                    inputRef.current.select();
                                    document.execCommand('copy');
                                    setActionTitle('Copied!');
                                    setTimeout(() => {
                                        setActionTitle('Copy');
                                    }, 1000);
                                }}
                            />
                        )}
                    </>
                )}
            </Stack>

            <ShowErrors
                errors={errors}
                mt={2}
                severity="error"
                variant="filled"
            />

            <Stack direction="row" justifyContent="flex-end" spacing={2} mt={2}>
                <Grid>
                    <Tooltip
                        title={
                            initialValues.is_enabled
                                ? t('User must be disabled')
                                : ''
                        }
                        placement="top"
                    >
                        <Box>
                            <Button
                                disabled={
                                    isFetchingDetail ||
                                    isSaving ||
                                    initialValues.is_enabled
                                }
                                onClick={() => {
                                    handleDeleteUser(formValues);
                                }}
                            >
                                {t('Delete user')}
                            </Button>
                        </Box>
                    </Tooltip>
                </Grid>

                <Grid>
                    <Button
                        disabled={isFetchingDetail || isSaving}
                        onClick={handleSubmit(handleClickSave)}
                        type="button"
                    >
                        {isSaving ? `${t('Loading')}...` : t('Save')}
                    </Button>
                </Grid>
            </Stack>
        </form>
    );
};

UserForm.propTypes = {
    changeInput: PropTypes.func,
    errors: PropTypes.array,
    fetchRandomPassword: PropTypes.func,
    formValues: PropTypes.object,
    handleClickSave: PropTypes.func,
    handleSubmit: PropTypes.func,
    initialValues: PropTypes.object,
    isFetchingDetail: PropTypes.bool,
    isFetchingPassword: PropTypes.bool,
    isSaving: PropTypes.bool,
    prepareDelete: PropTypes.func,
    roles: PropTypes.array,
    rolesDictionary: PropTypes.object,
};

const mapStateToProps = createStructuredSelector({
    errors: selectors.getErrorsSave,
    formValues: selectors.getValues,
    initialValues: selectors.getInitialValues,
    isFetchingDetail: selectors.getIsFetchingUserDetail,
    isFetchingPassword: selectors.getIsFetchingRandomPassword,
    isSaving: selectors.getIsSaving,
});

const mapDispatchToProps = (dispatch) => ({
    changeInput: (field, value) =>
        dispatch(change(`${NAME}/form`, field, value)),
    fetchRandomPassword: (onSuccessCallback) =>
        dispatch(actions.fetchRandomPassword(onSuccessCallback)),
    prepareDelete: (item) => dispatch(actions.prepareDelete(item)),
    handleClickSave: (values) => dispatch(actions.save(values)),
});

const FormContainer = reduxForm({
    enableReinitialize: true,
    form: `${NAME}/form`,
    validate,
})(UserForm);

export default connect(mapStateToProps, mapDispatchToProps)(FormContainer);
